import {
  ButtonBase,
  Checkbox,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { Period } from "hooks/periodIndex";
import { usePeriodNameFormatter } from "hooks/usePeriodNameFormatter";
import React from "react";
import { PeriodSummary, PlanBackupSummary } from "src/store/api/generatedApi";
import {
  deployPlanFormSlice,
  selectDeployBackupPlanSelected,
  selectDeployPlanFormIsSubmitting,
  selectDeployPlanPeriodSelected,
  selectIsBackupPlanPreviewed,
  selectIsDeployPlanPreviewed,
} from "src/store/slices/deployPlanFormSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { PlanId } from "components/common/boundary/PlanId";

interface Props {
  periods: PeriodSummary[];
  backups: PlanBackupSummary[];
  planId: PlanId;
}

interface BackupProps {
  title: string;
  planId: PlanId;
  backupPlanId: PlanId;
  backupPeriod: Period;
}

const Backup = ({ title, planId, backupPlanId, backupPeriod }: BackupProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isSelected = useAppSelector((state) =>
    selectDeployBackupPlanSelected(state, planId, backupPlanId)
  );

  const isPreviewed = useAppSelector((state) =>
    selectIsDeployPlanPreviewed(state, planId, {
      planId: backupPlanId,
      period: backupPeriod,
    })
  );

  const isSubmitting = useAppSelector((state) =>
    selectDeployPlanFormIsSubmitting(state, planId)
  );

  const handleOnChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      dispatch(
        deployPlanFormSlice.actions.addBackup([
          planId,
          { planId: backupPlanId, period: backupPeriod },
        ])
      );
      dispatch(
        deployPlanFormSlice.actions.setPreview([
          planId,
          {
            planId: backupPlanId,
            period: backupPeriod,
            title,
            isBackup: true,
          },
        ])
      );
    } else {
      dispatch(
        deployPlanFormSlice.actions.removeBackup([
          planId,
          { planId: backupPlanId, period: backupPeriod },
        ])
      );
    }
  };

  const handleOnClick = () => {
    dispatch(
      deployPlanFormSlice.actions.setPreview([
        planId,
        {
          planId: backupPlanId,
          period: backupPeriod,
          title,
          isBackup: true,
        },
      ])
    );
  };
  return (
    <Stack direction="row" ml={1} spacing={1}>
      <Checkbox
        checked={isSelected}
        onChange={handleOnChange}
        disabled={isSubmitting}
        sx={{ gridColumn: "4", padding: 0, paddingLeft: 0 }}
      />
      <ButtonBase
        onClick={handleOnClick}
        sx={{
          justifySelf: "flex-start",
          textAlign: "left",
          gridColumn: "5 / -1",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            textAlign: "left",
            fontWeight: isPreviewed
              ? 900
              : theme.typography.subtitle2.fontWeight,
          }}
        >
          {title}
        </Typography>
      </ButtonBase>
    </Stack>
  );
};

const PeriodWithBackups = ({
  backups,
  planId,
  periodNumber,
  periodName,
}: {
  backups: (Omit<PlanBackupSummary, "plan_id"> & { plan_id: PlanId })[];
  planId: PlanId;
  periodNumber: Period;
  periodName: string;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isSelected = useAppSelector((state) =>
    selectDeployPlanPeriodSelected(state, planId, periodNumber)
  );
  const isPreviewed = useAppSelector((state) =>
    selectIsDeployPlanPreviewed(state, planId, {
      planId,
      period: periodNumber,
    })
  );
  const isSubmitting = useAppSelector((state) =>
    selectDeployPlanFormIsSubmitting(state, planId)
  );
  const isBackupPreviewed = useAppSelector((state) =>
    selectIsBackupPlanPreviewed(
      state,
      planId,
      backups.map((backup) => backup.plan_id),
      periodNumber
    )
  );

  const handleOnChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      dispatch(
        deployPlanFormSlice.actions.addPeriod({ planId, period: periodNumber })
      );
      dispatch(
        deployPlanFormSlice.actions.setPreview([
          planId,
          {
            planId,
            period: periodNumber,
            title: "",
            isBackup: false,
          },
        ])
      );
    } else {
      dispatch(
        deployPlanFormSlice.actions.removePeriod({
          planId,
          period: periodNumber,
        })
      );
    }
  };

  const handleOnClick = () => {
    dispatch(
      deployPlanFormSlice.actions.setPreview([
        planId,
        {
          planId,
          period: periodNumber,
          title: "",
          isBackup: false,
        },
      ])
    );
  };

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1} p={0.5}>
      <Checkbox
        sx={{ padding: 0 }}
        checked={isSelected}
        onChange={handleOnChange}
        disabled={isSubmitting}
      />
      <ButtonBase
        onClick={handleOnClick}
        sx={{ gridColumn: "2/-2", justifySelf: "flex-start" }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: isPreviewed ? 900 : theme.typography.h6.fontWeight,
          }}
        >
          {periodName}
        </Typography>
      </ButtonBase>
      {backups.length > 0 ? (
        <Stack>
          <Typography
            variant="subtitle2"
            sx={{
              gridColumn: "3/-1",
              fontWeight: isBackupPreviewed
                ? 900
                : theme.typography.subtitle2.fontWeight,
            }}
          >
            {t("backups")}
          </Typography>
          {backups.map((backup) => {
            return (
              <Backup
                key={backup.plan_id}
                title={backup.display_name}
                planId={planId}
                backupPeriod={periodNumber}
                backupPlanId={backup.plan_id}
              />
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
};

export const Periods = ({ periods, backups, planId }: Props) => {
  const periodNameFormatter = usePeriodNameFormatter();

  return (
    <Stack direction="row">
      {periods
        .filter((period) => period.is_deployable)
        .map((period) => (
          <React.Fragment key={period.period}>
            <PeriodWithBackups
              key={period.period}
              backups={
                period.period === 1
                  ? backups.filter(
                      (
                        backup: PlanBackupSummary
                      ): backup is Omit<PlanBackupSummary, "plan_id"> & {
                        plan_id: PlanId;
                      } => backup.plan_id !== null
                    )
                  : []
              }
              planId={planId}
              periodNumber={period.period as Period}
              periodName={periodNameFormatter(period.period as Period, {
                startTimestamp: period.start_timestamp,
                endTimestamp: period.end_timestamp,
                name: period.name,
              })}
            />
            <Divider />
          </React.Fragment>
        ))}
    </Stack>
  );
};
